/**
 * component-dual-image-slideshow.js
 * Thin sync layer for the "Dual image slideshow" section.
 *
 * The two ist-slideshow elements intentionally omit data-section-id,
 * so component-image-with-text-slideshow.js leaves them alone.
 * This controller wires both tracks together with shared nav controls.
 */

class DualIstSlideshow {
  constructor(el) {
    this.el = el;

    // Both tracks use ist-slideshow markup but have no data-section-id
    const tracks = Array.from(el.querySelectorAll('.ist-slideshow'));
    if (tracks.length < 2) return;

    this.leftTrack  = tracks[0];
    this.rightTrack = tracks[1];

    this.leftSlides  = Array.from(this.leftTrack.querySelectorAll('.ist-slideshow__slide'));
    this.rightSlides = Array.from(this.rightTrack.querySelectorAll('.ist-slideshow__slide'));

    this.count   = this.leftSlides.length;
    this.current = 0;

    // Wire zoom buttons on each track independently
    this._initZoom(this.leftTrack,  this.leftSlides);
    this._initZoom(this.rightTrack, this.rightSlides);

    if (this.count <= 1) return;

    this.autoplay     = el.dataset.autoplay === 'true';
    this.speed        = parseInt(el.dataset.speed, 10) || 5000;
    this.transition   = el.dataset.transition || 'fade';
    this.pauseOnHover = el.dataset.pauseOnHover === 'true';
    this.timer        = null;

    if (this.transition === 'slide') {
      this.leftTrack.classList.add('ist-slideshow--slide');
      this.rightTrack.classList.add('ist-slideshow--slide');
    }

    // Shared nav controls live outside the ist-slideshow elements
    this.prevBtn = el.querySelector('.dis-arrow--prev');
    this.nextBtn = el.querySelector('.dis-arrow--next');
    this.dots    = Array.from(el.querySelectorAll('.dis-dot'));

    this.prevBtn && this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn && this.nextBtn.addEventListener('click', () => this.next());
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.index, 10)));
    });

    if (this.autoplay) {
      this.startAutoplay();
      if (this.pauseOnHover) {
        el.addEventListener('mouseenter', () => this.stopAutoplay());
        el.addEventListener('mouseleave', () => this.startAutoplay());
      }
    }

    this._initSwipe();
  }

  _initZoom(trackEl, slides) {
    const zoomBtn = trackEl.querySelector('.ist-slideshow__zoom');
    if (!zoomBtn) return;
    zoomBtn.addEventListener('click', () => {
      const slide = slides[this.current];
      if (!slide || (!slide.dataset.zoomSrc && !slide.querySelector('img'))) return;
      if (window.istGetModal) {
        window.istGetModal().open({ slides, current: this.current }, this.current);
      }
    });
  }

  goTo(index) {
    if (index === this.current) return;

    const prev   = this.current;
    this.current = ((index % this.count) + this.count) % this.count;

    [this.leftSlides, this.rightSlides].forEach((slides) => {
      if (!slides.length) return;

      if (this.transition === 'slide') {
        const exitEl = slides[prev];
        exitEl.classList.add('ist-slideshow__slide--exiting');
        const onEnd = () => {
          exitEl.classList.remove('ist-slideshow__slide--exiting');
          exitEl.removeEventListener('transitionend', onEnd);
        };
        exitEl.addEventListener('transitionend', onEnd);
      }

      slides[prev].classList.remove('is-active');
      slides[prev].setAttribute('aria-hidden', 'true');
      slides[this.current].classList.add('is-active');
      slides[this.current].setAttribute('aria-hidden', 'false');
    });

    if (this.dots[prev]) {
      this.dots[prev].classList.remove('is-active');
      this.dots[prev].setAttribute('aria-selected', 'false');
    }
    if (this.dots[this.current]) {
      this.dots[this.current].classList.add('is-active');
      this.dots[this.current].setAttribute('aria-selected', 'true');
    }
  }

  next() { this.goTo(this.current + 1); }
  prev() { this.goTo(this.current - 1); }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stopAutoplay() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  _initSwipe() {
    let startX = 0;
    this.el.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });
    this.el.addEventListener('touchend', (e) => {
      const delta = e.changedTouches[0].clientX - startX;
      if (Math.abs(delta) > 40) { delta < 0 ? this.next() : this.prev(); }
    }, { passive: true });
  }
}

function initAllDualSlideshows() {
  document.querySelectorAll('.dis-container[data-section-id]').forEach((el) => {
    if (!el._disInstance) el._disInstance = new DualIstSlideshow(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllDualSlideshows);
} else {
  initAllDualSlideshows();
}

document.addEventListener('shopify:section:load', (e) => {
  e.target.querySelectorAll('.dis-container[data-section-id]').forEach((el) => {
    el._disInstance = new DualIstSlideshow(el);
  });
});

document.addEventListener('shopify:block:select', (e) => {
  const slide = e.target;
  if (!slide.classList.contains('ist-slideshow__slide')) return;
  const container = slide.closest('.dis-container');
  if (!container || !container._disInstance) return;
  const idx = parseInt(slide.dataset.index, 10);
  if (!isNaN(idx)) {
    container._disInstance.stopAutoplay();
    container._disInstance.goTo(idx);
  }
});

document.addEventListener('shopify:block:deselect', (e) => {
  const slide = e.target;
  if (!slide.classList.contains('ist-slideshow__slide')) return;
  const container = slide.closest('.dis-container');
  if (!container || !container._disInstance) return;
  const instance = container._disInstance;
  if (instance.autoplay) instance.startAutoplay();
});
